const STORAGE_KEY = "vf:breadcrumb-source";
const MAX_SEGMENTS = 6;

/**
 * Normalizes a browsed collection path into safe breadcrumb segments joined by "/".
 * Returns null for empty, traversal, protocol-bearing, or otherwise unexpected input.
 * The value only ever feeds internal breadcrumb hrefs, so anything odd is rejected.
 */
export function sanitizeFromPath(input: unknown): string | null {
	if (typeof input !== "string") return null;
	const trimmed = input.replace(/^\/+/, "").replace(/\/+$/, "");
	if (!trimmed) return null;
	if (trimmed.includes("..") || trimmed.includes(":")) return null;
	if (!/^[a-z0-9\-/]+$/i.test(trimmed)) return null;
	const parts = trimmed.split("/").filter(Boolean);
	if (parts.length === 0 || parts.length > MAX_SEGMENTS) return null;
	return parts.join("/");
}

/**
 * Records which collection the customer clicked from, keyed by the product's id.
 * `lang` (e.g. "en", "no") is stored alongside the path because collection slugs are
 * language-specific — it is intentionally not the full geo locale (e.g. "en-NO"), so a
 * country-only switch within the same language keeps the trail alive.
 */
export function writeBreadcrumbSource(id: string, path: string, lang: string): void {
	if (typeof window === "undefined") return;
	const clean = sanitizeFromPath(path);
	if (!id || !clean || !lang) return;
	try {
		window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ id, path: clean, lang }));
	} catch {
		// sessionStorage unavailable (private mode / quota) — silently skip.
	}
}

/**
 * Returns the source collection segments for `id`, or null when there is no stored
 * source, it belongs to a different product (stale entry), or it was recorded under a
 * different language (its slugs no longer match the current language's dictionary).
 */
export function readBreadcrumbSourceSegments(
	id: string | undefined,
	lang: string,
): string[] | null {
	if (typeof window === "undefined" || !id) return null;
	try {
		const raw = window.sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as { id?: unknown; path?: unknown; lang?: unknown };
		if (parsed?.id !== id || parsed?.lang !== lang) return null;
		const clean = sanitizeFromPath(parsed.path);
		return clean ? clean.split("/") : null;
	} catch {
		return null;
	}
}
